import {Alert, Anchor, Button, Checkbox, Group, Modal, Stack, Text} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {Link, useNavigate} from '@tanstack/react-router'
import {useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {useEditRights} from 'src/jobs/edit-rights-context'
import {formatJobDate, toApiDateString, toLocalDate} from 'src/jobs/job-schedule-format'
import {useProject} from 'src/projects/use-projects'

import {
    openPickups,
    type PickupSelection,
    selectAll,
    selectedCount,
    stockLines,
} from './pickup-selection'
import {PickupUnitsChecklist} from './pickup-units-checklist'
import {useCreatePickupJob, usePickupCandidates} from './use-work'

type AddPickupButtonProps = {
    projectId: string
    fromJobId: string | null
}

type AddPickupFormValues = {
    dateTbc: boolean
    date: Date
    selection: PickupSelection
}

export function AddPickupButton(props: AddPickupButtonProps) {
    const {canDispatch} = useEditRights()
    return canDispatch ? <AddPickupDialogButton {...props} /> : null
}

function AddPickupDialogButton({projectId, fromJobId}: AddPickupButtonProps) {
    const navigate = useNavigate()
    const {data: units = []} = usePickupCandidates(projectId)
    const {data: order} = useProject(projectId)
    const existingPickups = openPickups(order?.jobs ?? [])
    const createPickup = useCreatePickupJob(projectId)
    const [opened, setOpened] = useState(false)
    const form = useForm<AddPickupFormValues>({
        initialValues: {
            dateTbc: true,
            date: businessCalendarDate(),
            selection: {unitIds: [], stockCounts: {}},
        },
    })
    const {dateTbc, date, selection} = form.values
    const [error, setError] = useState<string | null>(null)

    if (units.length === 0) {
        return null
    }

    function open() {
        form.setValues({dateTbc: true, date: businessCalendarDate(), selection: selectAll(units)})
        setError(null)
        setOpened(true)
    }

    async function handleCreate() {
        setError(null)
        try {
            const job = await createPickup.mutateAsync({
                fromJobId,
                startDate: dateTbc ? null : toApiDateString(date),
                rentalAssetIds: selection.unitIds,
                stock: stockLines(units, selection),
            })
            setOpened(false)
            const when = dateTbc ? 'date TBC' : formatJobDate(date)
            if (job.otherPickupJobs.length > 0) {
                const numbers = [job, ...job.otherPickupJobs].map((pickup) => pickup.displayId)
                notifications.show({
                    color: 'green',
                    title: 'Pickups scheduled',
                    message: `${numbers.join(', ')} (one per site), ${when}.`,
                })
                return
            }
            notifications.show({
                color: 'green',
                title: 'Pickup scheduled',
                message: `${job.displayId}, ${when}.`,
            })
            await navigate({to: '/jobs/$jobId', params: {jobId: job.id}})
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Could not schedule the pickup')
        }
    }

    return (
        <>
            <Button variant="default" onClick={open}>
                + Pickup
            </Button>
            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title="Schedule pickup"
                size="lg"
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Untick units staying on site.
                    </Text>
                    {existingPickups.map((pickup) => (
                        <Alert
                            key={pickup.id}
                            color="blue"
                            variant="light"
                            data-testid="existing-pickup"
                        >
                            Existing pickup:{' '}
                            <Anchor
                                size="sm"
                                renderRoot={(props) => (
                                    <Link
                                        {...props}
                                        to="/jobs/$jobId"
                                        params={{jobId: pickup.id}}
                                    />
                                )}
                            >
                                {pickup.displayId}: {pickup.name}
                            </Anchor>{' '}
                            ({pickup.dateTbc ? 'date TBC' : formatJobDate(pickup.startDate)}). Add
                            another only for units it is not bringing back.
                        </Alert>
                    ))}
                    {error ? <Alert color="red">{error}</Alert> : null}
                    <PickupUnitsChecklist
                        units={units}
                        selection={selection}
                        onChange={(value) => form.setFieldValue('selection', value)}
                    />
                    <Checkbox
                        label="Date TBC"
                        {...form.getInputProps('dateTbc', {type: 'checkbox'})}
                    />
                    {dateTbc ? null : (
                        <DateInput
                            label="Pickup date"
                            value={date}
                            minDate={businessCalendarDate()}
                            onChange={(value) =>
                                value && form.setFieldValue('date', toLocalDate(value))
                            }
                        />
                    )}
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setOpened(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => void handleCreate()}
                            loading={createPickup.isPending}
                            disabled={selectedCount(selection) === 0}
                        >
                            Schedule pickup of {selectedCount(selection)}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    )
}
